import type React from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerFooter,
  Button,
  Box,
  Text,
  Flex,
} from '@chakra-ui/react';
import { FunnelSimple, X, MagnifyingGlass } from 'phosphor-react';
import { FormProvider, UseFormReturn } from 'react-hook-form';

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  onClearFilters?: () => void;
  onApplyFilters?: () => void;
  hasActiveFilters?: boolean;
  formMethods?: UseFormReturn<any>;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

export function FilterDrawer({
  isOpen,
  onClose,
  children,
  title = 'Filtros',
  onClearFilters,
  onApplyFilters,
  hasActiveFilters = false,
  formMethods,
  size = 'md',
}: FilterDrawerProps) {
  const handleApplyFilters = () => {
    if (onApplyFilters) {
      onApplyFilters();
    }
    onClose();
  };

  const content = (
    <Drawer isOpen={isOpen} onClose={onClose} size={size} placement="right" preserveScrollBarGap>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader borderBottomWidth="1px">
          <Flex align="center" justify="space-between">
            <Flex align="center" gap={2}>
              <FunnelSimple size={20} />
              <Text fontSize="lg" fontWeight="semibold">
                {title}
              </Text>
            </Flex>
            <DrawerCloseButton position="static" />
          </Flex>
        </DrawerHeader>

        <DrawerBody p={6}>
          <Box mb={4}>
            {hasActiveFilters && onClearFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={onClearFilters}
                leftIcon={<X size={16} />}
                mb={4}
                w="full"
              >
                Limpar todos os filtros
              </Button>
            )}
          </Box>
          {children}
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px">
          <Button variant="outline" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleApplyFilters}
            leftIcon={<MagnifyingGlass size={18} />}
          >
            Filtrar
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );

  // Se formMethods foi passado, envolve o drawer com FormProvider
  // para manter o contexto do formulário dentro do portal
  if (formMethods) {
    return <FormProvider {...formMethods}>{content}</FormProvider>;
  }

  return content;
}
